// Mirrors services.services / services.bookings / service_staff / clients /
// memberships / loyalty_configs in CliqSuite_DB_Architecture.dc.html.
import { z } from "zod";

export const serviceSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  name: z.string(),
  category: z.string(),
  service_type: z.enum(["standard", "subscription"]).default("standard"),
  duration_minutes: z.number().int().nullable(),
  price: z.number().int().default(0),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  billing_cycle: z.enum(["monthly", "quarterly", "annual"]).nullable(),
  visits_per_cycle: z.number().int().nullable(),
  is_active: z.boolean().default(true),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Service = z.infer<typeof serviceSchema>;

export const bookingSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  client_id: z.string().uuid(),
  service_id: z.string().uuid(),
  staff_id: z.string().uuid().nullable(),
  starts_at: z.string(),
  service_name: z.string(),
  price: z.number().int(),
  status: z.enum([
    "booked",
    "confirmed",
    "checked_in",
    "in_service",
    "completed",
    "paid",
    "cancelled",
    "no_show",
  ]).default("booked"),
  is_walk_in: z.boolean().default(false),
  deposit: z.number().int().default(0),
  tip: z.number().int().default(0),
  paid_at: z.string().nullable(),
  pay_method: z.string().nullable(),
  notes: z.string().nullable(),
  created_by_staff_id: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Booking = z.infer<typeof bookingSchema>;

export const serviceStaffSchema = z.object({
  id: z.string().uuid(),
  service_id: z.string().uuid(),
  staff_id: z.string().uuid(),
});
export type ServiceStaff = z.infer<typeof serviceStaffSchema>;

export const clientSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  name: z.string(),
  phone: z.string().nullable(),
  note: z.string().nullable(),
  avatar_color: z.string().nullable(),
  visit_count: z.number().int().default(0),
  lifetime_spend: z.number().int().default(0),
  crm_member_id: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Client = z.infer<typeof clientSchema>;

export const membershipSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  client_id: z.string().uuid(),
  plan_service_id: z.string().uuid(),
  status: z.enum(["active", "expiring", "cancelled"]).default("active"),
  started_on: z.string(),
  next_bill_on: z.string().nullable(),
  visits_used: z.number().int().default(0),
  price: z.number().int(),
  points: z.number().int().default(0),
  lifetime_points: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Membership = z.infer<typeof membershipSchema>;

export const serviceLoyaltyConfigSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  points_per_1000_minor_units: z.string().default("1.0"),
  member_multiplier: z.string().default("2.0"),
  welcome_bonus: z.number().int().default(100),
  visit_points: z.number().int().default(15),
  tiers: z.array(z.record(z.string(), z.unknown())).default([]),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ServiceLoyaltyConfig = z.infer<typeof serviceLoyaltyConfigSchema>;
